import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Checkbox,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  styled,
} from "@mui/material";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getWorkTimeDuration } from "../../../../../app/common";
import { useAppSelector } from "../../../../../app/hooks";
import DayOfWeekDate from "../../../../../common/components/functional/DayOfWeekDate/DayOfWeekDate";
import { ReactComponent as EditFilled } from "../../../../../common/icons/editFilled.svg";
import { AssignmentById } from "../../../../talents/talentAPI";
import {
  BANK_HOLIDAY_ABSENCE_TYPE,
  WORKTIME_BANK_LEAVE_ABSENCE_TYPE,
} from "../../../constants";
import { assignmentsAvailableForEntry } from "../../Shared/functions";
import StartEndTimeFields from "../../Shared/StartEndTimeFields";
import { AbsenceListEntry } from "./AbsenceHoursList";
import { LunchBreak } from "../../../entriesAPI";
import { SalaryType } from "../../../../orders/types/salaryType";

interface Props {
  entry: AbsenceListEntry;
  setEntryToEdit: (entry: AbsenceListEntry) => void;
  handleCheckboxUpdate: (id: string, checked: boolean) => void;
  inputErrors: { [code: string]: boolean };
  setInputErrors: (inputErrors: { [code: string]: boolean }) => void;
  talentId?: string;
  assignments: AssignmentById[];
  updateEntry: (updatedEntry: AbsenceListEntry) => void;
  createNewEntryRow: (start: number) => void;
  absenceType?: string;
}

const AbsenceHourRow = React.memo(
  ({
    entry,
    setEntryToEdit,
    handleCheckboxUpdate,
    inputErrors,
    setInputErrors,
    talentId,
    assignments,
    updateEntry,
    createNewEntryRow,
    absenceType,
  }: Props) => {
    const { t } = useTranslation();

    const translatedWorkTitles = useAppSelector(
      (state) => state.localized.workTitle,
    );

    // Custom styled Select component
    const CustomSelect = styled(Select)(({ theme }) => ({
      padding: theme.spacing(0),
      backgroundColor: "#FFFFFF",
      "& .MuiSelect-select": {
        paddingTop: 4,
        paddingBottom: 4,
      },
    }));

    const handleCheckboxChange = (checked: boolean) => {
      handleCheckboxUpdate(entry.id, checked);
    };

    const updateLunchBreak = (entryStartEnd: {
      start: number;
      end: number;
    }) => {
      const newEntryLength =
        (entryStartEnd?.end - entryStartEnd?.start) / 60000;
      const shouldSetLunchBreak =
        entry.defaultLunchBreak?.setAutomaticallyAfter &&
        newEntryLength >= entry.defaultLunchBreak?.setAutomaticallyAfter;
      const updatedLunchBeak: LunchBreak | null = shouldSetLunchBreak
        ? entry.lunchBreak
          ? {
              start:
                entryStartEnd.start + (entry.lunchBreak.start - entry.start),
              duration: entry.lunchBreak.duration,
            }
          : {
              start:
                entryStartEnd.start +
                (entry.defaultLunchBreak.startDelta || 180) * 60000,
              duration: entry.defaultLunchBreak.length || 30,
            }
        : null;

      return updatedLunchBeak;
    };

    const handleTimeChange = (updatedData: { start: number; end: number }) => {
      // Adjust lunch break accordingly
      const updatedLunchBeak = updateLunchBreak(updatedData);
      const updatedEntry = {
        ...entry,
        start: updatedData.start,
        end: updatedData.end,
        lunchBreak: updatedLunchBeak,
      } as AbsenceListEntry;
      updateEntry(updatedEntry);
    };

    const handleChangeAssignment = (assignmentId: string) => {
      const updatedEntry = {
        ...entry,
        assignmentId,
      } as AbsenceListEntry;

      updateEntry(updatedEntry);
    };

    const handleChangeCustomerCostCenter = (customerCostCenterId: string) => {
      const customerCostCenterName = costCenterOptions.find(
        (option) => option.id === customerCostCenterId,
      )?.name;

      const updatedEntry = {
        ...entry,
        customerCostCenterId: customerCostCenterId,
        customerCostCenterName,
      } as AbsenceListEntry;

      updateEntry(updatedEntry);
    };

    const handleCreateNewEntryRow = () => {
      createNewEntryRow(entry.start);
    };

    const handleNotBillableClick = (checked: boolean) => {
      const updatedEntry = {
        ...entry,
        isBillable: !checked,
      } as AbsenceListEntry;
      updateEntry(updatedEntry);
    };
    const isChecked = entry.checked;

    // Filter assignments based on the entry's start and end time
    const assignmentsForEntryDate = useMemo(() => {
      return assignmentsAvailableForEntry(assignments, entry);
    }, [assignments, entry]);
    const entryAssignment = useMemo(() => {
      return assignments.find(
        (assignment) => assignment.assignmentId === entry.assignmentId,
      );
    }, [assignments, entry.assignmentId]);

    const costCenterOptions = useMemo(() => {
      if (!talentId) {
        return []; // Return empty array if no talentId
      }

      return assignmentsForEntryDate
        .filter((assignment) => assignment.assignmentId === entry.assignmentId) // Filter by current assignmentId
        .flatMap((assignment) => assignment.details.custCostCenters) // Flatten the cost centers
        .filter(
          (value, index, self) =>
            self.findIndex((v) => v.id === value.id) === index,
        ); // Remove duplicates based on 'id'
    }, [talentId, assignmentsForEntryDate, entry.assignmentId]);

    return (
      entry && (
        <TableRow
          sx={{
            // If row is unchecked, disable pointer events and lower opacity to make it look disabled
            ...(isChecked ? {} : { pointerEvents: "none" }),
            ...(!isChecked ? { opacity: "0.6" } : { opacity: "1" }),
          }}
        >
          <TableCell
            sx={{
              borderBottom: "none",
              padding: "0px",
              // If row is unchecked, enable pointer events to make checkbox clickable
              ...(isChecked ? {} : { pointerEvents: "auto" }),
            }}
          >
            <Checkbox
              checked={isChecked || false}
              disabled={
                // Disable checkbox if long order and no assignments available
                // or if absence type is time bank and entry is for short order
                (absenceType === WORKTIME_BANK_LEAVE_ABSENCE_TYPE &&
                  entry.isShift) ||
                (!entry.isShift &&
                  !!(talentId && assignmentsForEntryDate.length === 0))
              }
              onChange={(e) => handleCheckboxChange(e.target.checked)}
              sx={{
                padding: "0px",
                marginRight: "8px",
              }}
            />
          </TableCell>
          <TableCell
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              borderBottom: "none",
              paddingLeft: "8px",
            }}
          >
            <DayOfWeekDate
              start={entry.start}
              backgroundColor={isChecked ? "#FF505A" : undefined}
            />
          </TableCell>

          <TableCell sx={{ borderBottom: "none", padding: "0px" }}>
            <CustomSelect
              size="small"
              fullWidth
              displayEmpty
              disabled={!talentId || entry?.version !== 0} // Disable if there is no talentId or entry already exists
              value={entry?.assignmentId || ""}
              onChange={(e) => {
                const assignmentId = (e.target as HTMLInputElement).value;
                if (assignmentId) {
                  handleChangeAssignment(assignmentId);
                }
              }}
              renderValue={(selectedId) => {
                // ** EXISTING ENTRY **
                // If there's no talentId (view to create new entry) or entry is a shift (part of short order), display entry information directly
                if (!talentId || entry?.isShift) {
                  return (
                    <Box
                      sx={{
                        maxWidth: "200px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {`${entry.workLocationName} - ${translatedWorkTitles[entry.workTitleId] || ""}`}
                    </Box>
                  );
                }

                // ** NEW ENTRY **
                // Check if there are options available
                const hasOptions = assignmentsForEntryDate.length > 0;

                // Display "No options" if there are no options available, otherwise "Valitse..."
                if (!selectedId) {
                  return hasOptions
                    ? t("common:select...")
                    : t("hourEntries:noSelectableAssignments");
                }

                // Find the assignment based on the selected assignmentId
                const selectedAssignment = assignmentsForEntryDate.find(
                  (assignment) => assignment.assignmentId === selectedId,
                );

                if (!selectedAssignment) {
                  return t("common:select..."); // Fall back to placeholder if assignment is not found
                }

                return (
                  <Box
                    sx={{
                      maxWidth: "200px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {`${selectedAssignment.details.workLocationName} - ${
                      translatedWorkTitles[
                        selectedAssignment.details.workTitleId
                      ] || ""
                    }`}
                  </Box>
                );
              }}
            >
              {/* Only show menu items if there is a talentId and there are options */}
              {talentId && assignmentsForEntryDate.length > 0 ? (
                assignmentsForEntryDate.map((option) => (
                  <MenuItem
                    key={option.assignmentId}
                    value={option.assignmentId}
                  >
                    {option.details.workLocationName}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>
                  {t("hourEntries:noSelectableAssignments")}
                </MenuItem>
              )}
            </CustomSelect>
          </TableCell>

          <TableCell sx={{ borderBottom: "none" }}>
            <CustomSelect
              size="small"
              fullWidth
              displayEmpty
              disabled={!talentId || !costCenterOptions.length} // Disable if no talentId or no options
              value={entry.customerCostCenterId || ""}
              onChange={(e) => {
                const customerCostCenterId = (e.target as HTMLInputElement)
                  .value;
                if (customerCostCenterId) {
                  handleChangeCustomerCostCenter(customerCostCenterId);
                }
              }}
              renderValue={(selectedId) => {
                // If there is no talentId, or no selected value
                if (!selectedId) {
                  return t("common:select...");
                }

                // Check if the selected customerCostCenterId is valid
                const selectedCostCenter = costCenterOptions.find(
                  (option) => option.id === selectedId,
                );

                // If the selected cost center is invalid, fallback to placeholder
                if (talentId && !selectedCostCenter) {
                  return t("common:select...");
                }

                return (
                  <Box
                    sx={{
                      maxWidth: "100px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {selectedCostCenter?.name || entry.customerCostCenterName}
                  </Box>
                );
              }}
            >
              {/* Only show menu items if there is a talentId and options are available */}
              {talentId && costCenterOptions.length > 0 ? (
                costCenterOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>
                  {t("hourEntries:noSelectableCostCenters")}
                </MenuItem>
              )}
            </CustomSelect>
          </TableCell>

          <TableCell
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: "165px",
              borderBottom: "none",
              padding: "0px",
            }}
          >
            <StartEndTimeFields
              requireValue={true}
              id={`shift_${entry.id}`}
              disabled={false}
              inputErrors={inputErrors}
              setInputErrors={setInputErrors}
              start={entry.start}
              end={entry.end}
              plannedStart={entry.plannedStart}
              update={handleTimeChange}
            />
          </TableCell>

          <TableCell
            sx={{
              borderBottom: "none",
              whiteSpace: "nowrap",
              fontSize: "16px",
              paddingRight: "6px",
            }}
          >
            {getWorkTimeDuration(
              new Date(entry.end),
              new Date(entry.start),
              entry.lunchBreak?.duration,
            )}
          </TableCell>

          <TableCell
            sx={{
              borderBottom: "none",
              padding: "0px 4px 0px 4px",
            }}
          >
            {entry?.version !== 0 && (
              <EditFilled
                onClick={() =>
                  !Object.values(inputErrors).includes(true) &&
                  setEntryToEdit(entry)
                }
                style={{
                  width: "20px",
                  height: "20px",
                  marginTop: "6px",
                  cursor: "pointer",
                  padding: "2px",
                  opacity: Object.values(inputErrors).includes(true)
                    ? "0.4"
                    : "1",
                }}
              />
            )}
          </TableCell>
          {talentId && (
            <TableCell
              sx={{
                borderBottom: "none",
                padding: "0px 8px 0px 0px",
              }}
            >
              <AddIcon
                onClick={() => handleCreateNewEntryRow()}
                style={{
                  width: "24px",
                  height: "24px",
                  marginTop: "6px",
                  cursor: "pointer",
                }}
              />
            </TableCell>
          )}
          <TableCell
            sx={{
              borderBottom: "none",
              padding: "0px 8px 0 0",
              width: "100px",
            }}
          >
            <Checkbox
              // isBillable cannot be set for bank holidays or monthly salary entries, so disable option.
              disabled={
                entryAssignment?.employmentSalaryType === SalaryType.Monthly ||
                absenceType === BANK_HOLIDAY_ABSENCE_TYPE
              }
              checked={entry?.isBillable !== true}
              onChange={(e) => handleNotBillableClick(e.target.checked)}
              sx={{
                padding: "0px",
                marginRight: "8px",
              }}
            />
          </TableCell>
        </TableRow>
      )
    );
  },
);

export default AbsenceHourRow;
